import { ShieldCheck, Sparkles, Quote } from "lucide-react";
import { assetUrl } from "@/lib/api";

const Eyebrow = ({ children }) => <p className="text-sm font-semibold text-brand uppercase tracking-wider">{children}</p>;

export const About = ({ cfg }) => !cfg.about_text ? null : (
  <section id="about" className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 grid md:grid-cols-[0.8fr_1.2fr] gap-8">
    <div>
      <Eyebrow>Who we are</Eyebrow>
      <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-2" data-testid="landing-about-title">{cfg.about_title || "About us"}</h2>
    </div>
    <p className="text-base sm:text-lg text-stone-600 leading-relaxed whitespace-pre-line" data-testid="landing-about-text">{cfg.about_text}</p>
  </section>
);

export const Facilities = ({ cfg }) => (!cfg.facilities?.length && !cfg.activities?.length) ? null : (
  <section id="facilities" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
    <Eyebrow>Facilities &amp; activities</Eyebrow>
    <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-2 mb-10">A safe, joyful place to grow</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cfg.facilities.map((f) => (
        <div key={f.title} className="bg-white rounded-3xl border p-6" data-testid="landing-facility-card">
          <ShieldCheck className="w-6 h-6 text-brand mb-4" />
          <h3 className="text-lg font-semibold">{f.title}</h3>
          {f.description && <p className="text-sm text-stone-500 mt-1">{f.description}</p>}
        </div>
      ))}
    </div>
    {cfg.activities?.length > 0 && (
      <div className="flex flex-wrap gap-2 mt-8">
        {cfg.activities.map((a) => (
          <span key={a} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent-soft text-sm font-medium" data-testid="landing-activity-chip">
            <Sparkles className="w-3.5 h-3.5 text-brand" /> {a}
          </span>
        ))}
      </div>
    )}
  </section>
);

export const Gallery = ({ cfg }) => !cfg.gallery?.length ? null : (
  <section id="gallery" className="bg-brand-soft">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
      <Eyebrow>Gallery</Eyebrow>
      <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-2 mb-8">Moments from our campus</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        {cfg.gallery.map((g, i) => (
          <img key={g + i} src={assetUrl(g)} alt={`${cfg.institute_name} gallery ${i + 1}`} loading="lazy" data-testid="landing-gallery-image"
            className={`w-full object-cover rounded-2xl ${i % 5 === 0 ? "h-64 sm:h-80 md:row-span-2 md:h-full" : "h-40 sm:h-48"}`} />
        ))}
      </div>
    </div>
  </section>
);

export const Testimonials = ({ cfg }) => !cfg.testimonials?.length ? null : (
  <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24">
    <Eyebrow>Parents say</Eyebrow>
    <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-2 mb-10">Loved by families</h2>
    <div className="grid md:grid-cols-2 gap-5">
      {cfg.testimonials.map((t) => (
        <figure key={t.name + t.text} className="bg-white rounded-3xl border p-6 sm:p-8" data-testid="landing-testimonial">
          <Quote className="w-7 h-7 text-accent-brand mb-3" />
          <blockquote className="text-stone-700 leading-relaxed">{t.text}</blockquote>
          <figcaption className="mt-4 text-sm"><span className="font-semibold">{t.name}</span>{t.role && <span className="text-stone-500"> · {t.role}</span>}</figcaption>
        </figure>
      ))}
    </div>
  </section>
);
